/**
 * Cria uma função debounced que atrasa a invocação de `func` até depois que
 * `wait` milissegundos tenham passado desde a última vez que a função debounced foi invocada.
 * 
 * @param {Function} func - A função a ser debounced
 * @param {number} wait - Milissegundos de atraso
 * @returns {Function} - Função debounced
 */
export function debounce(func, wait = 300) {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Cria uma versão debounced de uma função assíncrona que retorna uma Promise.
 * Útil para AsyncSelect do react-select.
 * 
 * @param {Function} asyncFunc - A função assíncrona a ser debounced
 * @param {number} wait - Milissegundos de atraso (padrão: 500ms)
 * @returns {Function} - Função assíncrona debounced
 */
export function debounceAsync(asyncFunc, wait = 500) {
  let timeout;
  let latestResolve;
  let latestReject;

  return function executedFunction(...args) {
    return new Promise((resolve, reject) => {
      // Armazena os callbacks mais recentes
      latestResolve = resolve;
      latestReject = reject;

      // Limpa o timeout anterior
      if (timeout) {
        clearTimeout(timeout);
      }

      // Define novo timeout
      timeout = setTimeout(async () => {
        try {
          const result = await asyncFunc(...args);
          // Resolve apenas a Promise mais recente
          if (latestResolve === resolve) {
            resolve(result);
          }
        } catch (error) {
          // Rejeita apenas a Promise mais recente
          if (latestReject === reject) {
            reject(error);
          }
        }
      }, wait);
    });
  };
}
